import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { NGXLogger } from 'ngx-logger';

import { UserService } from '../../../auth/user/user.service';
import { I18n } from '../library/i18n';
import { TranslationsFile } from '../model/translation';
import { TranslationLoaderService } from './translation-loader.service';

export interface Locale {
  /** The language ID, corresponding to the language file name. */
  readonly id: string;

  /** The language short code, as displayed in the UI dropdown. */
  readonly shortCode: string;

  /** The language’s name for itself, as displayed in the UI dropdown. */
  readonly localName: string;

  /** The countries for which this language is the default. */
  readonly countryDefault?: readonly string[];
}

@Injectable({
  providedIn: 'root',
})
export class I18nService extends I18n<TranslationsFile, Signal<string>> {
  private readonly userService = inject(UserService);
  private readonly logger = inject(NGXLogger);

  static readonly languageLocalStorageKey = 'cdf-language';

  /**
   * The languages supported by the application.
   *
   * The first language in the list is the fallback language.
   */
  static readonly supportedLocales: readonly Locale[] = [
    { id: 'en-GB', shortCode: 'EN', localName: 'English' },
    { id: 'de-DE', shortCode: 'DE', localName: 'Deutsch', countryDefault: ['DE'] },
    { id: 'es-ES', shortCode: 'ES', localName: 'español', countryDefault: ['ES'] },
    { id: 'fr-FR', shortCode: 'FR', localName: 'français', countryDefault: ['FR'] },
    { id: 'nl-NL', shortCode: 'NL', localName: 'nederlands', countryDefault: ['NL'] },
    { id: 'pt-PT', shortCode: 'PT', localName: 'português', countryDefault: ['PT'] },
  ];

  /** The language received as input to the web component. */
  readonly languageFromInput: WritableSignal<string | undefined>;

  /** The language selected by the user in the UI. This can be updated using the `setUiLanguage` method. */
  readonly languageFromUi: Signal<string | undefined>;
  readonly #languageFromUi: WritableSignal<string | undefined>;

  constructor() {
    // The signals for determining the active language are initialised here,
    // but cannot be saved to `this` until `super()` is called.

    const languageFromUi = signal<string | undefined>(undefined);
    const languageFromInput = signal<string | undefined>(undefined);
    const languageFromPreviousUi = localStorage.getItem(I18nService.languageLocalStorageKey) || undefined;
    const userCountry = signal<string | undefined>(undefined);
    const languageFromCountry = computed(() => {
      const country = userCountry();
      return country && I18nService.supportedLocales.find((lang) => lang.countryDefault?.includes(country))?.id;
    });

    const fallbackLanguage = I18nService.supportedLocales[0].id;

    /**
     * The active language, determined by the following order of precedence:
     *
     * 1. The language selected by the user in the CDF UI since the last page load, if it is supported.
     * 2. The language received as input to the web component, if it is supported.
     * 3. The language selected by the user in the CDF UI on a previous page load, if it is supported.
     * 4. The default language for the user’s country, if set.
     * 5. The global fallback language.
     */
    const activeLanguage: Signal<string> = computed(
      () =>
        I18nService.resolveLocale(languageFromUi()) ||
        I18nService.resolveLocale(languageFromInput()) ||
        I18nService.resolveLocale(languageFromPreviousUi) ||
        I18nService.resolveLocale(languageFromCountry()) ||
        fallbackLanguage,
    );

    super(inject(TranslationLoaderService), activeLanguage);

    // Now that `super()` has been called, the signals can be saved to `this`.

    this.languageFromInput = languageFromInput;
    this.#languageFromUi = languageFromUi;
    this.languageFromUi = languageFromUi.asReadonly();

    this.userService.user().then(
      (country) => userCountry.set(country.countryCode),
      (error: unknown) => this.logger.error(error),
    );
  }

  /**
   * Sets the language which the user has selected in the UI.
   *
   * This applies immediately, and is saved to local storage.
   * This has the highest precedence for determining the active language.
   */
  setUiLanguage(language: string): void {
    localStorage.setItem(I18nService.languageLocalStorageKey, language);
    this.#languageFromUi.set(language);
  }

  /**
   * Checks whether the language for the given language ID is supported by the application.
   *
   * If it is not exactly supported, but a language
   *
   * If it is supported, the same language ID if it is. Otherwise, `undefined`.
   */
  private static resolveLocale(langId: string | undefined): string | undefined {
    if (!langId) return undefined;

    const locale =
      // Check through the entire list for an exact match first
      I18nService.supportedLocales.find((locale) => I18nService.exactMatch(locale, langId)) ||
      // If no exact match is found, check for a language match (e.g. en-US -> en-GB)
      I18nService.supportedLocales.find((locale) => I18nService.languageMatch(locale, langId));

    return locale?.id;
  }

  private static exactMatch(locale: Locale, langId: string): boolean {
    return langId === locale.id;
  }

  private static languageMatch(locale: Locale, langId: string): boolean {
    return langId.slice(0, 2) === locale.id.slice(0, 2);
  }
}

/**
 * Prepares i18n for use in a component.
 *
 * This is needed so that translation signals can be scoped to the component.
 */
export function useI18n() {
  return inject(I18nService).use();
}
